import { supabase } from '@/integrations/supabase/client';

const BUCKET = 'solicitacao-media';

export interface SolicitacaoListItem {
  id: string;
  categoria: { id: string; nome: string };
  cidade: string;
  status: string;
  n_orcamentos: number;
  criado_em: string;
}

export interface OrcamentoConsumer {
  id: string;
  empresa: { id: string; nome: string; logo_url: string | null; rating_media: number; rating_total: number };
  valor: number;
  mensagem: string | null;
  status: string;
  criado_em: string;
}

export interface SolicitacaoDetalhe {
  id: string;
  categoria: { id: string; nome: string };
  cidade: string;
  descricao: string;
  fotos: string[];
  status: string;
  criado_em: string;
  orcamento_aceite_id: string | null;
  orcamentos: OrcamentoConsumer[];
}

export interface SolicitacaoEmpresaItem {
  solicitacao_id: string;
  categoria: { id: string; nome: string };
  cidade: string;
  descricao: string;
  fotos: string[];
  criado_em: string;
  primeiro_nome: string;
  ja_orcei: boolean;
  meu_orcamento: { valor: number; status: string } | null;
}

/** Cliente cria um pedido aberto; devolve o id. */
export async function criarSolicitacao(input: { categoriaId: string; cidade: string; descricao: string }): Promise<string> {
  const { data, error } = await supabase.rpc('criar_solicitacao', {
    p_categoria_id: input.categoriaId, p_cidade: input.cidade, p_descricao: input.descricao,
  });
  if (error) throw error;
  return data as string;
}

/** Grava o array de storage paths das fotos no pedido. */
export async function anexarFotosSolicitacao(id: string, fotos: string[]): Promise<void> {
  const { error } = await supabase.rpc('anexar_fotos_solicitacao', { p_id: id, p_fotos: fotos });
  if (error) throw error;
}

/** Lista os pedidos do cliente. */
export async function listarMinhasSolicitacoes(): Promise<SolicitacaoListItem[]> {
  const { data, error } = await supabase.rpc('listar_minhas_solicitacoes');
  if (error) throw error;
  return (data as unknown as SolicitacaoListItem[]) ?? [];
}

/** Detalhe de um pedido do cliente (ou null). */
export async function obterSolicitacaoConsumer(id: string): Promise<SolicitacaoDetalhe | null> {
  const { data, error } = await supabase.rpc('obter_solicitacao_consumer', { p_id: id });
  if (error) throw error;
  return (data as unknown as SolicitacaoDetalhe) ?? null;
}

/** Aceita um orçamento; devolve o cliente_potencial_id (para abrir o chat). */
export async function aceitarOrcamento(orcamentoId: string): Promise<string> {
  const { data, error } = await supabase.rpc('aceitar_orcamento', { p_orcamento_id: orcamentoId });
  if (error) throw error;
  return data as string;
}

/** Cancela um pedido aberto. */
export async function cancelarSolicitacao(id: string): Promise<void> {
  const { error } = await supabase.rpc('cancelar_solicitacao', { p_id: id });
  if (error) throw error;
}

/** Empresa: lista os pedidos abertos que combinam com o seu perfil. */
export async function listarSolicitacoesEmpresa(): Promise<SolicitacaoEmpresaItem[]> {
  const { data, error } = await supabase.rpc('listar_solicitacoes_empresa');
  if (error) throw error;
  return (data as unknown as SolicitacaoEmpresaItem[]) ?? [];
}

/** Empresa: envia um orçamento estruturado; devolve o id. */
export async function enviarOrcamento(input: { solicitacaoId: string; valor: number; mensagem?: string }): Promise<string> {
  const { data, error } = await supabase.rpc('enviar_orcamento', {
    p_solicitacao_id: input.solicitacaoId, p_valor: input.valor, p_mensagem: input.mensagem ?? '',
  });
  if (error) throw error;
  return data as string;
}

/** Upload de uma foto para solicitacao-media em {solicitacaoId}/{uuid}.{ext}; devolve o storage path. */
export async function uploadFotoSolicitacao(solicitacaoId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${solicitacaoId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type });
  if (error) throw error;
  return path;
}

/** Signed URL (1h) para uma foto do pedido. */
export async function getFotoSolicitacaoUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}
